import React, { useMemo } from 'react';
import { IFileData, TFileExtension } from 'types';
import { useBlobDownload } from 'hooks/useBlobDownload';
import { regexForStartLinks } from 'appConstants';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import { DragAndDropForFileNew } from 'components/DragAndDropForFile/DragAndDropForFileNew';
import { FileField } from 'components/EntityForm/FileField';
import { FormRow } from 'components/EntityForm/FormRow';
import { FormInputNew } from 'components/Input/FormInputNew';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import { TooltipForElementNew } from 'components/Tooltips/TooltipForElementNew';

const billTemplateName = 'bill_template';
const adImageName = 'image';
const adImageExtensions: TFileExtension[] = ['png', 'jpg', 'jpeg'];

interface IProps {
	billTemplateFileData: IFileData;
	billAdvertisingImageData: IFileData;
	billTemplateFilePath?: Nullable<string>;
	billAdvertisingImage?: Nullable<string>;
	changeBillTemplateFileData: (value: IFileData) => void;
	changeBillAdvertisingImageData: (value: IFileData) => void;
	changeBillAdvertisingLink: (value: string) => void;
	onDeleteBillAdvertisingImage: () => void;
	onHtmlFileSizeExceeded: () => void;
	onImageFileSizeExceeded: () => void;
	onInvalidHtmlFormat: () => void;
	onInvalidImageFormat: () => void;
	onUploadError: () => void;
	onTooManyFiles: () => void;
	onDownloadError?: () => void;
}

export const BillTemplateBlockNew: React.FC<IProps> = ({
	billTemplateFileData,
	billAdvertisingImageData,
	billTemplateFilePath = null,
	billAdvertisingImage = null,
	changeBillTemplateFileData,
	changeBillAdvertisingImageData,
	changeBillAdvertisingLink,
	onDeleteBillAdvertisingImage,
	onHtmlFileSizeExceeded,
	onImageFileSizeExceeded,
	onInvalidHtmlFormat,
	onInvalidImageFormat,
	onUploadError,
	onTooManyFiles,
	onDownloadError,
}) => {
	const { t: tForm } = useTranslation('form');
	const { t: tError } = useTranslation('error');
	const savedAdImageExtension = adImageExtensions.find((el) => billAdvertisingImage?.endsWith(`.${el}`)) ?? 'png';
	const downloadBillTemplate = useBlobDownload({
		filePath: billTemplateFilePath,
		fileExtension: 'html',
		fileName: billTemplateName,
		onError: onDownloadError,
	});
	const downloadAdImage = useBlobDownload({
		filePath: billAdvertisingImage,
		fileExtension: savedAdImageExtension,
		fileName: adImageName,
		onError: onDownloadError,
	});

	const validationRules = useMemo<Rule[]>(
		() => [
			{
				validator: (_, value) => {
					if (value && !value.match(regexForStartLinks)) {
						return Promise.reject(new Error(tError('linkMustStartWithHttp')));
					}
					return Promise.resolve();
				},
			},
		],
		[tError],
	);

	return (
		<FormRow>
			<div className="flex flex-col gap-2">
				<Text translationNameSpace="form" translationKey="billTemplateHeading" size="ml" weight="bold" />
				<DragAndDropForFileNew
					hint="htmlFileOnly"
					accept={['html']}
					maxFileSizeMB={10}
					testId="BillTemplateUpload"
					onUpload={changeBillTemplateFileData}
					onSizeExceeded={onHtmlFileSizeExceeded}
					onInvalidFormat={onInvalidHtmlFormat}
					onUploadError={onUploadError}
					onTooManyFiles={onTooManyFiles}
				/>
				{billTemplateFileData.fileName && (
					<FileField fileName={billTemplateFileData.fileName} testId="BillTemplateFile" />
				)}
				{!billTemplateFileData.fileName && billTemplateFilePath && (
					<FileField
						fileName={`${billTemplateName}.html`}
						testId="BillTemplateFile"
						onDownload={downloadBillTemplate}
					/>
				)}
			</div>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<div className="flex items-start gap-2">
						<Text translationNameSpace="form" translationKey="adImageHeading" size="ml" weight="bold" />
						<TooltipForElementNew title={tForm('advertisementTooltip')}>
							<div className="flex">
								<Icon name={IconName.infoCircle} size="lg" className="text-purple-07-new" />
							</div>
						</TooltipForElementNew>
					</div>
					<DragAndDropForFileNew
						hint="PngJpgOrJpeg(recommendSize)"
						accept={adImageExtensions}
						maxFileSizeMB={1}
						testId="AdImageUpload"
						onUpload={changeBillAdvertisingImageData}
						onSizeExceeded={onImageFileSizeExceeded}
						onInvalidFormat={onInvalidImageFormat}
						onUploadError={onUploadError}
						onTooManyFiles={onTooManyFiles}
					/>
					{billAdvertisingImageData.fileName && (
						<FileField
							fileName={billAdvertisingImageData.fileName}
							testId="AdImageFile"
							onDelete={onDeleteBillAdvertisingImage}
						/>
					)}
					{!billAdvertisingImageData.fileName && billAdvertisingImage && (
						<FileField
							fileName={`${adImageName}.${savedAdImageExtension}`}
							testId="AdImageFile"
							onDownload={downloadAdImage}
							onDelete={onDeleteBillAdvertisingImage}
						/>
					)}
				</div>
				<FormInputNew
					testId="billAdvertisingLinkField"
					name="billAdvertisingLink"
					label="advertisingLinkLabel"
					validationRules={validationRules}
					onChange={changeBillAdvertisingLink}
				/>
			</div>
		</FormRow>
	);
};
